import PropTypes from "prop-types";
import React, { Component } from "react";
import Select, { components, createFilter } from "react-select";
import Creatable from "react-select/creatable";
import { classes } from "components/Utility";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import { fontColor } from "components/Shared/systemColors";
import Tooltip from "@mui/material/Tooltip";
import Zoom from "@mui/material/Zoom";

export const ReactSelectClassName = "react-select-dd-control";
export const ReactLargeSelectClassName = "react-large-select-container";

const getReactSelectStyles = ({ noPadding }) => {
  return {
    control: (provided) => ({
      ...provided,
      borderColor: null,
      boxShadow: null,
      backgroundColor: "initial",
      minHeight: "0px",
      border: "none",
    }),
    dropdownIndicator: () => ({
      color: fontColor.primary,
    }),
    clearIndicator: () => ({
      color: fontColor.primary,
    }),
    indicatorSeparator: () => ({
      display: "none"
    }),
    menu: (provided) => ({
      ...provided,
      width: "fit-content",
      minWidth: "100%",
    }),
    menuList: (provided) => ({
      ...provided,
      width: "fit-content",
      minWidth: "100%",
    }),
    option: (provided, state) => ({
      ...provided,
      whiteSpace: "nowrap",
      backgroundColor: null,
      color: state.isSelected ? fontColor.primary : null,
      width: "fit-content",
      minWidth: "100%",
      ":active": {
        backgroundColor: "rgba(0, 0, 0, .24)",
      },
      ":hover": {
        backgroundColor: "rgba(0, 0, 0, .12)"
      },
      ".checkbox": {
        pointerEvents: "none",
        width: "fit-content !important",
        //Needed as React-md media queries overwrite this when dropdown portal placed in small container
        padding: "0px",
        height: "25px",
      },
      "label": {
        color: "inherit",
        whiteSpace: "nowrap",
      },
      "i": {
        fontSize: "25px !important",
      },
      //Needed as React-md media queries overwrite this when dropdown portal placed in small container
      ".md-btn--icon": {
        height: "25px !important",
        width: "40px !important",
        padding: "0px !important",
      }
    }),
    valueContainer: ({ padding, ...provided }) => ({
      ...provided,
      fontSize: 13,
      padding: noPadding ? 0 : padding,
    }),
    tooltip: () => ({
      color: "var(--tooltip-font-color)",
      margin: 0,
    }),
  };
};

class MSelect extends Component {
  constructor(props) {
    super(props);
    this.ref = React.createRef();
    this.getExistingOption = this.getExistingOption.bind(this);
    this.stringToNewOption = this.stringToNewOption.bind(this);
    this.getValueFromOptions = this.getValueFromOptions.bind(this);
    this.getSelectComponent = this.getSelectComponent.bind(this);
    this.getSubComponents = this.getSubComponents.bind(this);
    this.getValue = this.getValue.bind(this);
    this.clearValue = this.clearValue.bind(this);
    this.onChange = this.onChange.bind(this);
    this.state = {
      styles: getReactSelectStyles(props),
      portalMenuTarget: document.querySelector("body"),
    };
  }

  getExistingOption(value) {
    let options = this.getOptions();
    return options && options.find(option => (option.value ? option.value.toString() : option.value) === value) || value;
  }

  stringToNewOption(string) {
    return { value: string, label: string };
  }

  getSelectComponent() {
    const { creatable } = this.props;
    return creatable ? (
      Creatable
    ) : (
      Select
    );
  }

  getSubComponents({ styles: { tooltip } }) {
    const subComponents = {};
    const { enableAllOption, allOption, isMulti, shortMulti, showArrow } = this.props;
    const all = this.getValueFromOptions(allOption);
    if (isMulti) {
      subComponents["Option"] = ({ children, ...props }) =>
        <components.Option
          className={"md-text-field"}
          {...props}
        >
          <FormControlLabel
            className={`checkbox`}
            control={<Checkbox checked={props.isSelected} onChange={() => { }} />}
            label={children}
          />
        </components.Option>;
      if (shortMulti)
        subComponents["MultiValue"] = ({ getValue, children }) => {
          const value = getValue();
          if (value && value.length && children === value[0].label)
            return (
              <Tooltip
                slots={{ transition: Zoom }}
                title={<>{
                  value.map(({ label }, index) =>
                    <p
                      key={`${label}-${index}`}
                      style={tooltip()}>
                      {label}
                    </p>
                  )
                }</>}>
                <span>
                  {`${value.length - ((enableAllOption && value[0].label === all.label) ? 1 : 0)} Selected`}
                </span>
              </Tooltip >
            );
          return null;
        };
    }
    if (!showArrow)
      subComponents["DropdownIndicator"] = () => null;
    return subComponents;
  }

  getValueFromOptions(value) {
    if (typeof value === "string") {
      value = this.getExistingOption(value);
      if (typeof value === "string")
        value = this.stringToNewOption(value);
    }
    return value;
  }

  getValue() {
    return this.ref.current.state.value.value;
  }

  clearValue() {
    this.ref.current.select.clearValue();
  }

  onChange(newValue) {
    let {
      value,
      isMulti,
      shortMulti,
      allOption,
      enableAllOption,
      onChange
    } = this.props;
    if (typeof onChange !== "function") return;
    let options = this.getOptions();
    const oldValue = value;
    if (isMulti && shortMulti) {
      allOption = this.getValueFromOptions(allOption);
      const newValueContainsAllOption = newValue.some(newSelectedValue => newSelectedValue.label === allOption.label);
      const oldValueContainsAllOption = oldValue && oldValue.length === options.length;
      if (enableAllOption) {
        if (!oldValueContainsAllOption && newValueContainsAllOption) {
          onChange(options);
          return;
        }
        else if (!newValueContainsAllOption && newValue.length === options.length) {
          onChange([]);
          return;
        }
      }
      onChange(newValue.filter(nv => nv.label !== allOption.label));
      return;
    }
    onChange(newValue);
  }

  getOptions = () => {
    let optionsResult = this.props.options?.map(option =>
        typeof option === "string" ?
          this.stringToNewOption(option) :
          option) ?? [];
    if (this.props.sortFunc) {
      optionsResult = [...optionsResult].sort(this.props.sortFunc);
    }
    return optionsResult;
  }

  onFocus = () => {
    if (this.props.enablePortal && this.props.getMenuPortalTarget && this.state.portalMenuTarget === document.querySelector("body")) {
      this.setState({ portalMenuTarget: this.props.getMenuPortalTarget() });
    }
    if (this.props.onFocus) this.props.onFocus();
  };

  getValue = () => {
    if (!this.props.value) return this.props.value;
    let valueResult;
    if (Array.isArray(this.props.value)) {
      valueResult = this.props.value.map(v => this.getValueFromOptions(v));
    } else {
      valueResult = this.getValueFromOptions(this.props.value);
    }
    return valueResult;
  }

  render() {
    const {
      enableAllOption,
      shortMulti,
      className,
      enablePortal,
      isClearable,
      isSearchable,
      isMulti,
      standardOptionComponents,
      placeholder,
      label,
      itemLabel,
      itemValue,
      name,
    } = this.props;

    let {
      styles,
      portalMenuTarget,
    } = this.state;

    // copy the styles object so we arent modifying state
    styles = { ...styles };
    styles.menuPortal = base => ({ ...base, zIndex: 20 });

    let options = this.getOptions();
    let value = this.getValue();

    let defaultValue = this.props.defaultValue ? this.getValueFromOptions(this.props.defaultValue) : this.props.defaultValue;
    let allOption = this.props.allOption ? this.getValueFromOptions(this.props.allOption) : this.props.allOption;

    const SelectComponent = this.getSelectComponent();

    const valueProp = (value && value.length
        && options && value[0]?.label !== allOption?.label 
        && value.length === options?.length 
        && !standardOptionComponents)
      ? [allOption, ...value]
      : value;

    return (<SelectComponent
      className={classes(className, ReactSelectClassName)}
      classNamePrefix="react-select"
      defaultValue={defaultValue}
      value={valueProp}
      options={(isMulti && shortMulti && enableAllOption) ? [allOption, ...options] : options}
      components={standardOptionComponents ? null : this.getSubComponents({ styles })}
      menuPortalTarget={enablePortal && portalMenuTarget}
      onChange={this.onChange}
      closeMenuOnSelect={!isMulti}
      menuPlacement={this.props.menuPlacement || "bottom"}
      hideSelectedOptions={false}
      filterOption={createFilter({ ignoreAccents: false })} //speeds up searching and also will ignore accents on characters while searching which is nice
      onFocus={this.onFocus}
      ref={this.ref}
      isClearable={isClearable}
      isMulti={isMulti}
      styles={styles}
      placeholder={placeholder}
      label={label}
      itemLabel={itemLabel}
      itemValue={itemValue}
      name={name}
      isSearchable={isSearchable}
    />);
  }
}

MSelect.defaultProps = {
  options: [],
  enableAllOption: true,
  allOption: { value: "Select All", label: "Select All" },
  enablePortal: true,
  getMenuPortalTarget: null,
  creatable: false,
  noPadding: false,
  isMulti: false,
  shortMulti: true,
  onChange: null,
  isClearable: true,
  isSearchable: true,
  sortFunc: null,
  innerRef: null,
  showArrow: true,
  isMenuOpen: null,
};

MSelect.propTypes = {
  className: PropTypes.string,
  value: PropTypes.oneOfType([
    PropTypes.array,
    PropTypes.string,
    PropTypes.object,
  ]),
  defaultValue: PropTypes.oneOfType([
    PropTypes.array,
    PropTypes.string,
    PropTypes.object,
  ]),
  options: PropTypes.arrayOf(PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.string
  ])),
  allOption: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.string
  ]),
  enableAllOption: PropTypes.bool,
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
  isClearable: PropTypes.bool,
  isSearchable: PropTypes.bool,
  isMenuOpen: PropTypes.bool,
  isMulti: PropTypes.bool,
  shortMulti: PropTypes.bool,
  showArrow: PropTypes.bool,
  sortFunc: PropTypes.func,
  creatable: PropTypes.bool, // When using ceatable, your values in the options must be strings as the component calls .toLowerCase on the value
  enablePortal: PropTypes.bool,
  getMenuPortalTarget: PropTypes.func,
  innerRef: PropTypes.node,
  noPadding: PropTypes.bool,
  standardOptionComponents: PropTypes.bool, // Pass in true if you don't want to use the custom sub components. These appear incompatiable with MCard.
  placeholder: PropTypes.string,
  label: PropTypes.string,
  itemLabel: PropTypes.string,
  itemValue: PropTypes.string,
  name: PropTypes.string,
  menuPlacement: PropTypes.oneOf(["auto", "bottom", "top"]), // Controls where the menu opens relative to the control. Default is "bottom"
};

export default MSelect;